
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { Assessment, Recommendation, MaturityScoreResult, Category } from '@/types/assessment';
import { supabase } from '@/integrations/supabase/client';

// Generate PDF Blob from screenshot of assessment results (for email attachments)
export const generateScreenshotPDFBlob = async (
  contentRef: React.RefObject<HTMLElement>
): Promise<Blob | null> => {
  if (!contentRef.current) {
    console.error('Content reference not available');
    return null;
  }

  try {
    // Configure html2canvas with optimized settings for smaller file size
    const canvas = await html2canvas(contentRef.current, {
      scale: 1, // Lower scale to reduce file size significantly
      useCORS: true,
      backgroundColor: '#ffffff',
      width: contentRef.current.scrollWidth,
      height: contentRef.current.scrollHeight,
      scrollX: 0,
      scrollY: 0,
      logging: false,
      imageTimeout: 0,
      removeContainer: true
    });

    // Use JPEG with compression for smaller file size
    const imgData = canvas.toDataURL('image/jpeg', 0.7); // 70% quality for good balance
    
    // Create PDF with appropriate dimensions
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Page margins - increased to prevent text cutoff at page boundaries
    const margins = { top: 20, right: 15, bottom: 25, left: 15 };
    const contentWidth = pdfWidth - (margins.left + margins.right);
    const pageContentHeight = pdfHeight - (margins.top + margins.bottom);
    
    // Calculate image dimensions to fit the page
    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    let yPosition = margins.top;
    
    // If image is taller than page, split it across multiple pages
    if (imgHeight > pageContentHeight) {
      const pageHeight = pageContentHeight;
      const sliceHeight = Math.floor((pageHeight * canvas.width) / imgWidth);
      // Larger overlap to prevent text from being cut at page boundaries
      const overlap = Math.floor(canvas.width * 0.02); // 2% overlap to ensure smooth transitions

      for (let page = 0; page < Math.ceil(imgHeight / pageHeight); page++) {
        if (page > 0) {
          pdf.addPage();
        }

        const sourceY = Math.max(0, page * sliceHeight - (page > 0 ? overlap : 0));
        const sourceHeight = Math.min(
          sliceHeight + (page > 0 ? overlap : 0),
          canvas.height - sourceY
        );

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.ceil(sourceHeight);
        const pageCtx = pageCanvas.getContext('2d');

        if (pageCtx) {
          // Improve crispness at boundaries
          // @ts-ignore - property exists in CanvasRenderingContext2D
          pageCtx.imageSmoothingEnabled = false;
          pageCtx.drawImage(
            canvas,
            0, Math.round(sourceY), canvas.width, Math.round(sourceHeight),
            0, 0, canvas.width, Math.round(sourceHeight)
          );

          const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.7); // Use JPEG with compression
          const actualHeight = (sourceHeight * imgWidth) / canvas.width;
          pdf.addImage(pageImgData, 'JPEG', margins.left, yPosition, imgWidth, actualHeight);
        }
      }
    } else {
      pdf.addImage(imgData, 'JPEG', margins.left, yPosition, imgWidth, imgHeight);
    }
    
    return pdf.output('blob');
  } catch (error) {
    console.error('Error generating PDF blob:', error);
    return null;
  }
};

// Generate PDF from screenshot of assessment results (for direct download)
export const generateScreenshotPDF = async (
  assessment: Assessment,
  contentRef: React.RefObject<HTMLElement>
) => {
  const blob = await generateScreenshotPDFBlob(contentRef);
  
  if (!blob) {
    console.error('Failed to generate PDF');
    return;
  }

  const filename = `${assessment.title} - Assessment Results.pdf`;

  // Upload to Google Drive in the background (don't wait)
  uploadToGoogleDrive(blob, filename).catch(error => {
    console.error('Failed to upload to Google Drive:', error);
  });

  // Create download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Upload PDF to Google Drive
async function uploadToGoogleDrive(pdfBlob: Blob, filename: string): Promise<void> {
  try {
    // Convert blob to base64
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve, reject) => {
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
    });
    reader.readAsDataURL(pdfBlob);
    
    const fileData = await base64Promise;
    
    console.log('Uploading PDF to Google Drive...');
    
    // Call the edge function to upload to Google Drive
    const { data, error } = await supabase.functions.invoke('upload-to-google-drive', {
      body: {
        fileName: filename,
        fileData: fileData
      }
    });

    if (error) {
      console.error('Error uploading to Google Drive:', error);
      throw error;
    }

    console.log('Successfully uploaded to Google Drive:', data);
  } catch (error) {
    console.error('Failed to upload PDF to Google Drive:', error);
    throw error;
  }
}

// Generate standalone PDF from data (programmatic, not screenshot)
export const generateStandalonePDF = async (
  assessment: Assessment,
  categories: Category[],
  scoresByCategory: Record<string, {current: number, target: number}>,
  currentMaturity: MaturityScoreResult,
  targetMaturity: MaturityScoreResult,
  recommendations: Recommendation[]
): Promise<Blob> => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margins = { top: 20, right: 20, bottom: 20, left: 20 };
  const contentWidth = pageWidth - margins.left - margins.right;
  let yPosition = margins.top;

  // Helper function to check if we need a new page
  const checkPageBreak = (spaceNeeded: number) => {
    if (yPosition + spaceNeeded > pageHeight - margins.bottom) {
      pdf.addPage();
      yPosition = margins.top;
      return true;
    }
    return false;
  };

  // Header with orange gradient (matching assessment complete page)
  // Create gradient effect with orange colors
  const gradientHeight = 60;
  const gradientSteps = 50;
  for (let i = 0; i < gradientSteps; i++) {
    const ratio = i / gradientSteps;
    // From #e87a2e to #f97316 (hsl(25 85% 53%) to hsl(22 92% 53%))
    const r = Math.floor(232 + (249 - 232) * ratio);
    const g = Math.floor(122 + (115 - 122) * ratio);
    const b = Math.floor(46 + (22 - 46) * ratio);
    pdf.setFillColor(r, g, b);
    pdf.rect(0, (gradientHeight / gradientSteps) * i, pageWidth, gradientHeight / gradientSteps + 1, 'F');
  }
  
  // Title
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Assessment Complete!', pageWidth / 2, 25, { align: 'center' });
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Thank you for completing the ${assessment.title} Preview!`, pageWidth / 2, 35, { align: 'center' });
  pdf.text('If you want access to the full assessment, please request premium access.', pageWidth / 2, 42, { align: 'center' });
  
  yPosition = 75;
  pdf.setTextColor(0, 0, 0);

  // Maturity Scores Section
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Your Assessment Results', margins.left, yPosition);
  yPosition += 12;

  // Maturity cards side by side - matching web layout exactly
  const cardWidth = (contentWidth - 10) / 2;
  const cardHeight = 70;
  
  checkPageBreak(cardHeight + 10);
  
  // Helper function to get badge color based on level
  const getBadgeColor = (level: number): {r: number, g: number, b: number} => {
    if (level === 0) return {r: 0, g: 0, b: 0}; // Black for level 0
    if (level === 1) return {r: 251, g: 191, b: 36}; // Yellow (#fbbf24) for level 1
    if (level === 2) return {r: 251, g: 146, b: 60}; // Orange (#fb923c) for level 2
    if (level === 3) return {r: 234, g: 88, b: 12}; // Dark Orange (#ea580c) for level 3
    return {r: 34, g: 197, b: 94}; // Green (#22c55e) for level 4
  };
  
  // --- Current Maturity Card ---
  pdf.setFillColor(249, 250, 251);
  pdf.roundedRect(margins.left, yPosition, cardWidth, cardHeight, 3, 3, 'F');
  pdf.setDrawColor(229, 231, 235);
  pdf.roundedRect(margins.left, yPosition, cardWidth, cardHeight, 3, 3, 'S');
  
  // Card title
  pdf.setFontSize(10);
  pdf.setTextColor(107, 114, 128);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Current Maturity', margins.left + 5, yPosition + 8);
  
  // 2-column layout within card
  const gridStartY = yPosition + 14;
  const leftColX = margins.left + 5;
  const colWidth = (cardWidth - 14) / 2; // Split into two equal columns with spacing
  const rightColX = margins.left + 8 + colWidth;
  
  // LEFT COLUMN: Score display
  pdf.setFontSize(32);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'bold');
  pdf.text(currentMaturity.score.toFixed(1), leftColX, gridStartY + 14);
  
  pdf.setFontSize(9);
  pdf.setTextColor(107, 114, 128);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Score (0-4)', leftColX, gridStartY + 21);
  
  // RIGHT COLUMN: Colored badge with level number and label inside
  const currentColor = getBadgeColor(currentMaturity.level);
  const badgeWidth = colWidth - 2;
  const badgeHeight = 30;
  pdf.setFillColor(currentColor.r, currentColor.g, currentColor.b);
  pdf.roundedRect(rightColX, gridStartY, badgeWidth, badgeHeight, 3, 3, 'F');
  
  // Text inside the badge - white text (or black for yellow)
  const isYellow = currentMaturity.level === 1;
  pdf.setTextColor(isYellow ? 0 : 255, isYellow ? 0 : 255, isYellow ? 0 : 255);
  
  // Large level number centered in badge
  pdf.setFontSize(28);
  pdf.setFont('helvetica', 'bold');
  const badgeCenterX = rightColX + (badgeWidth / 2);
  pdf.text(`${currentMaturity.level}`, badgeCenterX, gridStartY + 14, { align: 'center' });
  
  // Level label below the number, inside the badge
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  const labelLines = pdf.splitTextToSize(currentMaturity.label, badgeWidth - 4);
  labelLines.forEach((line: string, idx: number) => {
    pdf.text(line, badgeCenterX, gridStartY + 21 + (idx * 3), { align: 'center' });
  });
  
  // Description text below the grid
  pdf.setFontSize(8);
  pdf.setTextColor(107, 114, 128);
  pdf.setFont('helvetica', 'normal');
  const currentDesc = pdf.splitTextToSize(currentMaturity.description || '', cardWidth - 10);
  pdf.text(currentDesc, margins.left + 5, yPosition + 49);
  
  // --- Target Maturity Card ---
  const targetCardX = margins.left + cardWidth + 10;
  pdf.setFillColor(249, 250, 251);
  pdf.roundedRect(targetCardX, yPosition, cardWidth, cardHeight, 3, 3, 'F');
  pdf.setDrawColor(229, 231, 235);
  pdf.roundedRect(targetCardX, yPosition, cardWidth, cardHeight, 3, 3, 'S');
  
  // Card title
  pdf.setFontSize(10);
  pdf.setTextColor(107, 114, 128);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Target Maturity', targetCardX + 5, yPosition + 8);
  
  const leftColX2 = targetCardX + 5;
  const rightColX2 = targetCardX + 8 + colWidth;
  
  // LEFT COLUMN: Score display
  pdf.setFontSize(32);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'bold');
  pdf.text(targetMaturity.score.toFixed(1), leftColX2, gridStartY + 14);
  
  pdf.setFontSize(9);
  pdf.setTextColor(107, 114, 128);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Score (0-4)', leftColX2, gridStartY + 21);
  
  // RIGHT COLUMN: Colored badge
  const targetColor = getBadgeColor(targetMaturity.level);
  pdf.setFillColor(targetColor.r, targetColor.g, targetColor.b);
  pdf.roundedRect(rightColX2, gridStartY, badgeWidth, badgeHeight, 3, 3, 'F');
  
  // Text inside the badge
  const isYellow2 = targetMaturity.level === 1;
  pdf.setTextColor(isYellow2 ? 0 : 255, isYellow2 ? 0 : 255, isYellow2 ? 0 : 255);
  
  // Large level number
  pdf.setFontSize(28);
  pdf.setFont('helvetica', 'bold');
  const badgeCenterX2 = rightColX2 + (badgeWidth / 2);
  pdf.text(`${targetMaturity.level}`, badgeCenterX2, gridStartY + 14, { align: 'center' });
  
  // Level label
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  const labelLines2 = pdf.splitTextToSize(targetMaturity.label, badgeWidth - 4);
  labelLines2.forEach((line: string, idx: number) => {
    pdf.text(line, badgeCenterX2, gridStartY + 21 + (idx * 3), { align: 'center' });
  });
  
  // Description text
  pdf.setFontSize(8);
  pdf.setTextColor(107, 114, 128);
  pdf.setFont('helvetica', 'normal');
  const targetDesc = pdf.splitTextToSize(targetMaturity.description || '', cardWidth - 10);
  pdf.text(targetDesc, targetCardX + 5, yPosition + 49);
  
  yPosition += cardHeight + 15;

  // Radar Chart Section (matching web page design)
  checkPageBreak(120); // Reserve space for the chart
  
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Assessment Results', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;
  
  // Draw radar chart
  const chartCenterX = pageWidth / 2;
  const chartCenterY = yPosition + 45;
  const chartRadius = 40;
  const maxScore = 4;
  
  // Draw concentric circles (grid)
  pdf.setDrawColor(224, 224, 224); // Light gray
  pdf.setLineWidth(0.5);
  for (let i = 1; i <= 4; i++) {
    const radius = (chartRadius / 4) * i;
    pdf.circle(chartCenterX, chartCenterY, radius, 'S');
  }
  
  // Draw axis lines and labels for each category
  const angleStep = (2 * Math.PI) / categories.length;
  const categoryPoints: {current: {x: number, y: number}, target: {x: number, y: number}}[] = [];
  
  categories.forEach((category, index) => {
    const angle = angleStep * index - Math.PI / 2; // Start from top
    const scores = scoresByCategory[category.id] || { current: 0, target: 0 };
    
    // Draw axis line
    pdf.setDrawColor(224, 224, 224);
    pdf.setLineWidth(0.5);
    const axisEndX = chartCenterX + Math.cos(angle) * chartRadius;
    const axisEndY = chartCenterY + Math.sin(angle) * chartRadius;
    pdf.line(chartCenterX, chartCenterY, axisEndX, axisEndY);
    
    // Calculate points for current and target scores
    const currentRadius = (scores.current / maxScore) * chartRadius;
    const targetRadius = (scores.target / maxScore) * chartRadius;
    
    categoryPoints.push({
      current: {
        x: chartCenterX + Math.cos(angle) * currentRadius,
        y: chartCenterY + Math.sin(angle) * currentRadius
      },
      target: {
        x: chartCenterX + Math.cos(angle) * targetRadius,
        y: chartCenterY + Math.sin(angle) * targetRadius
      }
    });
    
    // Draw category label
    const labelDistance = chartRadius + 15;
    const labelX = chartCenterX + Math.cos(angle) * labelDistance;
    const labelY = chartCenterY + Math.sin(angle) * labelDistance;
    
    pdf.setFontSize(10);
    pdf.setTextColor(67, 62, 67); // Dark text
    pdf.setFont('helvetica', 'bold');
    pdf.text(category.name, labelX, labelY, { align: 'center', maxWidth: 40 });
  });
  
  // Draw current score polygon (gray fill)
  if (categoryPoints.length > 0) {
    pdf.setFillColor(142, 145, 150); // Gray #8E9196
    pdf.setDrawColor(142, 145, 150);
    pdf.setLineWidth(2);
    
    // Start path
    pdf.lines(
      categoryPoints.map((p, i) => {
        if (i === 0) {
          return [p.current.x - categoryPoints[0].current.x, p.current.y - categoryPoints[0].current.y];
        }
        return [p.current.x - categoryPoints[i-1].current.x, p.current.y - categoryPoints[i-1].current.y];
      }),
      categoryPoints[0].current.x,
      categoryPoints[0].current.y,
      [1, 1],
      'FD',
      true
    );
    
    // Draw current score dots
    categoryPoints.forEach(p => {
      pdf.circle(p.current.x, p.current.y, 2, 'F');
    });
  }
  
  // Draw target score polygon (orange fill)
  if (categoryPoints.length > 0) {
    pdf.setFillColor(232, 122, 46, 0.5); // Orange #e87a2e with transparency
    pdf.setDrawColor(232, 122, 46);
    pdf.setLineWidth(2);
    
    // Start path
    pdf.lines(
      categoryPoints.map((p, i) => {
        if (i === 0) {
          return [p.target.x - categoryPoints[0].target.x, p.target.y - categoryPoints[0].target.y];
        }
        return [p.target.x - categoryPoints[i-1].target.x, p.target.y - categoryPoints[i-1].target.y];
      }),
      categoryPoints[0].target.x,
      categoryPoints[0].target.y,
      [1, 1],
      'FD',
      true
    );
    
    // Draw target score dots
    categoryPoints.forEach(p => {
      pdf.circle(p.target.x, p.target.y, 2, 'F');
    });
  }
  
  // Draw legend below chart
  yPosition = chartCenterY + chartRadius + 25;
  
  const legendY = yPosition;
  const legendCenterX = pageWidth / 2;
  
  // Current legend
  pdf.setFillColor(142, 145, 150);
  pdf.rect(legendCenterX - 30, legendY - 3, 8, 3, 'F');
  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Current', legendCenterX - 20, legendY);
  
  // Target legend
  pdf.setFillColor(232, 122, 46);
  pdf.rect(legendCenterX + 5, legendY - 3, 8, 3, 'F');
  pdf.text('Target', legendCenterX + 15, legendY);
  
  yPosition += 15;

  // Recommendations - Tree Structure
  if (recommendations.length > 0) {
    checkPageBreak(30);
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Recommendations', margins.left, yPosition);
    yPosition += 8;
    
    pdf.setFontSize(10);
    pdf.setTextColor(107, 114, 128);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Based on the gaps between your current and target states', margins.left, yPosition);
    yPosition += 12;
    
    // Group recommendations by category, then by question
    const grouped: Record<string, Record<string, { questionText: string; recommendations: Recommendation[] }>> = {};
    
    recommendations.forEach(rec => {
      if (!grouped[rec.categoryName]) {
        grouped[rec.categoryName] = {};
      }
      if (!grouped[rec.categoryName][rec.questionId]) {
        grouped[rec.categoryName][rec.questionId] = {
          questionText: rec.questionText,
          recommendations: []
        };
      }
      grouped[rec.categoryName][rec.questionId].recommendations.push(rec);
    });
    
    // Render tree structure
    Object.entries(grouped).forEach(([categoryName, questionGroups]) => {
      checkPageBreak(25);
      
      const totalRecs = Object.values(questionGroups).reduce((sum, g) => sum + g.recommendations.length, 0);
      
      // Category header with border
      pdf.setDrawColor(229, 231, 235);
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margins.left, yPosition, contentWidth, 10, 2, 2, 'FD');
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(categoryName, margins.left + 3, yPosition + 6);
      
      // Count on right side
      pdf.setFontSize(9);
      pdf.setTextColor(107, 114, 128);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`(${totalRecs} recommendation${totalRecs !== 1 ? 's' : ''})`, contentWidth + margins.left - 35, yPosition + 6);
      
      yPosition += 16;
      
      // Question groups
      Object.entries(questionGroups).forEach(([questionId, group]) => {
        checkPageBreak(15);
        
        // Question text with left border
        pdf.setDrawColor(224, 224, 224);
        pdf.setLineWidth(1);
        pdf.line(margins.left + 3, yPosition - 2, margins.left + 3, yPosition + (group.recommendations.length * 5) + 3);
        
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        const questionLines = pdf.splitTextToSize(group.questionText, contentWidth - 15);
        pdf.text(questionLines, margins.left + 7, yPosition);
        yPosition += Math.max(questionLines.length * 4, 5);
        
        pdf.setFontSize(8);
        pdf.setTextColor(107, 114, 128);
        pdf.text(`(${group.recommendations.length} recommendation${group.recommendations.length !== 1 ? 's' : ''})`, margins.left + 7, yPosition);
        yPosition += 5;
        
        // Individual recommendations as bullets
        group.recommendations.forEach(rec => {
          checkPageBreak(8);
          
          pdf.setFontSize(9);
          pdf.setTextColor(107, 114, 128);
          pdf.setFont('helvetica', 'normal');
          const recLines = pdf.splitTextToSize(`• ${rec.text}`, contentWidth - 20);
          pdf.text(recLines, margins.left + 10, yPosition);
          yPosition += recLines.length * 4 + 2;
        });
        
        yPosition += 3;
      });
      
      yPosition += 5;
    });
  }

  // Next Steps Section
  checkPageBreak(40);
  
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text('What happens next?', margins.left, yPosition);
  yPosition += 10;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(107, 114, 128);
  
  const nextSteps = [
    "• Your responses have been recorded",
    "• You'll receive a confirmation email shortly with your detailed results",
    "• Review the recommendations above to improve your maturity scores"
  ];
  
  nextSteps.forEach(step => {
    checkPageBreak(15);
    const stepLines = pdf.splitTextToSize(step, contentWidth - 30);
    pdf.text(stepLines, margins.left + 15, yPosition);
    yPosition += stepLines.length * 5 + 2;
  });

  yPosition += 10;

  // Footer
  const currentDate = new Date().toLocaleDateString();
  pdf.setFontSize(8);
  pdf.setTextColor(107, 114, 128);
  pdf.text(`Generated on ${currentDate}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  
  return pdf.output('blob');
};

// Generate PDF without DOM references (for email attachments) - uses standalone generation
export const generatePDFForEmail = generateStandalonePDF;

export const generatePDF = async (
  assessment: Assessment,
  categories: Category[],
  scoresByCategory: Record<string, {current: number, target: number}>,
  currentMaturity: MaturityScoreResult,
  targetMaturity: MaturityScoreResult,
  recommendations: Recommendation[],
  chartRef: React.RefObject<HTMLDivElement>
) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  let yPosition = 20;
  
  // Add title
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text(assessment.title, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;
  
  // Add subtitle
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Assessment Report', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 20;
  
  // Add maturity scores
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Maturity Scores', 20, yPosition);
  yPosition += 10;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Current Maturity: ${currentMaturity.score.toFixed(1)} (Level ${currentMaturity.level} - ${currentMaturity.label})`, 20, yPosition);
  yPosition += 6;
  pdf.text(`Target Maturity: ${targetMaturity.score.toFixed(1)} (Level ${targetMaturity.level} - ${targetMaturity.label})`, 20, yPosition);
  yPosition += 15;
  
  // Add chart
  if (chartRef.current) {
    const canvas = await html2canvas(chartRef.current);
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = pageWidth - 40;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, imgHeight);
    yPosition += imgHeight + 15;
  }
  
  // Add category scores
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Category Scores', 20, yPosition);
  yPosition += 10;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  categories.forEach(category => {
    // Check if we're near the bottom of the page
    if (yPosition > pdf.internal.pageSize.getHeight() - 30) {
      pdf.addPage();
      yPosition = 20;
    }
    
    const scores = scoresByCategory[category.id] || { current: 0, target: 0 };
    pdf.text(`${category.name}:`, 20, yPosition);
    yPosition += 5;
    pdf.text(`Current: ${scores.current.toFixed(1)}, Target: ${scores.target.toFixed(1)}`, 25, yPosition);
    yPosition += 8;
  });
  
  // Add recommendations
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  
  // Check if we're near the bottom of the page
  if (yPosition > pdf.internal.pageSize.getHeight() - 50) {
    pdf.addPage();
    yPosition = 20;
  } else {
    yPosition += 10;
  }
  
  pdf.text('Recommendations', 20, yPosition);
  yPosition += 10;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  recommendations.forEach((rec, index) => {
    // Check if we're near the bottom of the page
    if (yPosition > pdf.internal.pageSize.getHeight() - 40) {
      pdf.addPage();
      yPosition = 20;
    }
    
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${index + 1}. ${rec.categoryName}`, 20, yPosition);
    yPosition += 5;
    
    pdf.setFont('helvetica', 'normal');
    pdf.text(rec.text, 25, yPosition, { maxWidth: pageWidth - 45 });
    yPosition += 12;
    
    if (rec.actionItems && rec.actionItems.length > 0) {
      pdf.text('Action Items:', 25, yPosition);
      yPosition += 5;
      
      rec.actionItems.forEach(item => {
        pdf.text(`• ${item}`, 30, yPosition, { maxWidth: pageWidth - 50 });
        yPosition += 5;
      });
      
      yPosition += 5;
    }
  });
  
  // Add footer
  const currentDate = new Date().toLocaleDateString();
  pdf.setFontSize(8);
  pdf.text(`Generated on ${currentDate}`, pageWidth - 20, pdf.internal.pageSize.getHeight() - 10, { align: 'right' });
  
  // Save the PDF
  pdf.save(`${assessment.title} - Assessment Report.pdf`);
};
